<template>
  <el-drawer :title="dialogTitle" v-model="visible" direction="rtl" size="30%" :before-close="handleClose">
    <div class="dialog-content">
      <!-- 操作栏 -->
      <div class="action-bar">
        <el-button type="primary" :disabled="selectedImages.length === 0" @click="confirmSelection">
          确认选择 ({{ selectedImages.length }})
        </el-button>
        <el-button @click="selectAll" v-if="imgList.length > 0">
          {{ isAllSelected ? '取消全选' : '全选' }}
        </el-button>
        <el-button @click="clearSelection">清空选择</el-button>
      </div>

      <!-- 图片列表 -->
      <div class="image-container">
        <div v-for="(url, index) in imgList" :key="index" class="image-item" :class="{ selected: isSelected(url) }"
          @click="toggleSelection(url)">
          <div class="image-checkbox">
            <el-checkbox :model-value="isSelected(url)" @click.stop @change="toggleSelection(url)" />
          </div>

          <el-image :src="url" fit="contain" class="extracted-image">
            <template #error>
              <div class="image-error">
                <el-icon>
                  <Picture />
                </el-icon>
                <span>图片加载失败</span>
              </div>
            </template>
          </el-image>

          <!-- <div class="image-overlay">
            <el-icon class="preview-icon" @click.stop="previewImage(url)">
              <ZoomIn />
            </el-icon>
          </div> -->
        </div>

        <div v-if="imgList.length === 0" class="empty-state">
          <el-icon>
            <Picture />
          </el-icon>
          <p>没有提取到图片</p>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script setup>
import { ref, computed, defineEmits, defineExpose } from 'vue'
import { Picture, ZoomIn } from '@element-plus/icons-vue'

const emit = defineEmits(['confirm-selection'])

const visible = ref(false)
const dialogTitle = ref('选择图片')
const imgList = ref([])
const selectedImages = ref([])

// 计算属性
const isAllSelected = computed(() => {
  return selectedImages.value.length === imgList.value.length && imgList.value.length > 0
})

// 打开对话框
const open = (data) => {
  console.log('提取的图片数据:', data)
  imgList.value = Array.isArray(data) ? data : []
  selectedImages.value = [] // 清空之前的选择
  visible.value = true
  updateTitle()
}

// 更新标题
const updateTitle = () => {
  dialogTitle.value = `选择图片 (${selectedImages.value.length}/${imgList.value.length}张)`
}

// 检查图片是否被选中
const isSelected = (url) => {
  return selectedImages.value.includes(url)
}

// 切换选择状态
const toggleSelection = (url) => {
  const index = selectedImages.value.indexOf(url)
  if (index > -1) {
    selectedImages.value.splice(index, 1)
  } else {
    selectedImages.value.push(url)
  }
  updateTitle()
}

// 全选/取消全选
const selectAll = () => {
  if (isAllSelected.value) {
    selectedImages.value = []
  } else {
    selectedImages.value = [...imgList.value]
  }
  updateTitle()
}

// 清空选择
const clearSelection = () => {
  selectedImages.value = []
  updateTitle()
}

// 确认选择
const confirmSelection = () => {
  if (selectedImages.value.length > 0) {
    emit('confirm-selection', selectedImages.value)
    visible.value = false
  }
}

// 预览图片
const previewImage = (url) => {
  // 使用 el-image 的预览功能
  const images = document.querySelectorAll('.extracted-image')
  const imageEl = Array.from(images).find(img => img.src.includes(url))
  if (imageEl) {
    imageEl.click() // 触发预览
  }
}

// 关闭处理
const handleClose = (done) => {
  // 可以添加确认逻辑
  done()
}

// 关闭对话框
const close = () => {
  visible.value = false
  selectedImages.value = []
  imgList.value = []
}

defineExpose({ open, close })
</script>

<style lang="scss" scoped>
.dialog-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.action-bar {
  padding: 16px;
  border-bottom: 1px solid #e1e1e1;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.image-container {
  flex: 1;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  overflow-y: auto;
}

.image-item {
  position: relative;
  height: 150px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    .image-overlay {
      opacity: 1;
    }
  }

  &.selected {
    border-color: var(--el-color-primary);
  }
}

.image-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  // background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 4px;
}

.extracted-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f5f5f5;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.preview-icon {
  color: white;
  font-size: 24px;
  cursor: pointer;

  &:hover {
    color: var(--el-color-primary);
  }
}

.image-error {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;

  .el-icon {
    font-size: 32px;
    margin-bottom: 8px;
  }
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  color: #999;

  .el-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

// 响应式调整
@media (max-width: 768px) {
  .image-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .image-item {
    height: 120px;
  }

  .action-bar {
    flex-direction: column;
    align-items: stretch;

    .el-button {
      margin: 4px 0;
    }
  }
}
</style>